import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { APIProvider, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import { getMarkerColor, getCategoryColor, resetColorAssignments, getColorAssignments } from '../utils/markerUtils';
import { emptySet, emptyMap, noop } from './mapConstants';
import './Map.css';

// Helper function to check if a point is inside a polygon
function pointInPolygon(point, polygon) {
  const [lng, lat] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    const intersect = ((yi > lat) !== (yj > lat))
        && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }

  return inside;
}

const MAP_ID = 'ssn-gems-map';

// Helper function to convert hex color to RGB array
function hexToRgb(hex) {
  if (!hex) return [128, 128, 128];
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [128, 128, 128];
}

// Helper function to check if a color is dark (using relative luminance)
function isColorDark(rgb) {
  const [r, g, b] = rgb;
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

// Helper function to get opposite/inverted color
function getOppositeColor(rgb) {
  return [255 - rgb[0], 255 - rgb[1], 255 - rgb[2]];
}

// Minimum zoom level at which labels appear (0-21 scale, where 21 is closest)
// Lower number = labels appear at more zoomed out view (wider area visible)
const LABEL_MIN_ZOOM = 7;

// Component that manages deck.gl overlay
function DeckGLOverlay({ markers, selectedMarkers, onMarkerClick, onLoadComplete, styleColumn, labelColumn, hoveredMarkerId, onHover, colorRefreshKey }) {
  const map = useMap();
  const overlayRef = useRef(null);
  const layerRef = useRef(null);
  const [shouldShowLabels, setShouldShowLabels] = useState(false); // Track if zoom is sufficient for labels

  // Track zoom threshold crossings only (not every zoom change)
  useEffect(() => {
    if (!map) return;

    const checkZoomThreshold = () => {
      const zoom = map.getZoom();
      const labelsVisible = zoom >= LABEL_MIN_ZOOM;

      // Only update state if visibility changes (crossing threshold)
      setShouldShowLabels(prev => {
        if (prev !== labelsVisible) {
          console.log(`Labels ${labelsVisible ? 'shown' : 'hidden'} at zoom ${zoom}`);
          return labelsVisible;
        }
        return prev;
      });
    };

    // Get initial state
    checkZoomThreshold();

    // Listen for zoom changes
    const zoomListener = map.addListener('zoom_changed', checkZoomThreshold);

    return () => {
      window.google.maps.event.removeListener(zoomListener);
    };
  }, [map]);

  useEffect(() => {
    if (!map) return;

    // Wait for map to be fully initialized with a small delay
    const initTimeout = setTimeout(() => {
      console.log('DeckGLOverlay: Creating/updating overlay with', markers.length, 'markers');

      // Debug: Log unique style values
      if (styleColumn && markers.length > 0) {
        const styleColumnLower = styleColumn.toLowerCase().trim();
        const uniqueValues = [...new Set(markers.map(m => m[styleColumnLower]).filter(Boolean))];
        console.log('Style column:', styleColumn);
        console.log('Unique values in style column:', uniqueValues);
      }

      // Create deck.gl layer
      const scatterplotLayer = new ScatterplotLayer({
        id: 'markers-layer',
        data: markers,
        pickable: true,
        opacity: 1,
        stroked: true,
        filled: true,
        radiusScale: 1,
        radiusMinPixels: 7,
        radiusMaxPixels: 7,  // Fixed size
        lineWidthMinPixels: 2,
        lineWidthMaxPixels: 30,  // Allow border to grow much larger
        lineWidthScale: 1,
        getPosition: d => [d.longitude, d.latitude],
        getRadius: 8,  // Fixed radius for all markers
        getFillColor: d => {
          // Keep original fill color always
          let color;
          if (styleColumn) {
            const styleColumnLower = styleColumn.toLowerCase().trim();
            const categoryValue = d[styleColumnLower];
            color = getCategoryColor(categoryValue);
          } else {
            color = getMarkerColor(d.color);
          }
          const rgb = hexToRgb(color);
          return rgb;
        },
        getLineColor: d => {
          const isSelected = selectedMarkers.has(d.id);
          const isHovered = hoveredMarkerId === d.id;

          // Get the fill color
          let color;
          if (styleColumn) {
            const styleColumnLower = styleColumn.toLowerCase().trim();
            const categoryValue = d[styleColumnLower];
            color = getCategoryColor(categoryValue);
          } else {
            color = getMarkerColor(d.color);
          }
          const fillRgb = hexToRgb(color);

          if (isSelected) {
            // Black border for selected (or white if fill is black)
            const isBlackFill = fillRgb[0] < 30 && fillRgb[1] < 30 && fillRgb[2] < 30;
            return isBlackFill ? [255, 255, 255] : [0, 0, 0];
          }

          if (isHovered) {
            // Opposite/inverse color for hover
            return getOppositeColor(fillRgb);
          }

          // Default white border
          return [255, 255, 255];
        },
        getLineWidth: d => {
          const isSelected = selectedMarkers.has(d.id);
          const isHovered = hoveredMarkerId === d.id;
          const width = isSelected ? 20 : (isHovered ? 8 : 2);
          if (isSelected || isHovered) {
            console.log('Marker', d.id, 'lineWidth:', width, 'selected:', isSelected, 'hovered:', isHovered);
          }
          return width;
        },
        onClick: info => {
          if (info.object) {
            onMarkerClick(info.object.id);
          }
        },
        onHover: info => {
          if (onHover) {
            onHover(info.object ? info.object.id : null);
          }
        },
        updateTriggers: {
          getRadius: [selectedMarkers, hoveredMarkerId],
          getFillColor: [styleColumn, selectedMarkers, hoveredMarkerId, colorRefreshKey],
          getLineColor: [selectedMarkers, hoveredMarkerId, colorRefreshKey],
          getLineWidth: [selectedMarkers, hoveredMarkerId],
        },
      });

      layerRef.current = scatterplotLayer;

      // Create text layer for labels (if labelColumn is set and zoom is sufficient)
      const layers = [scatterplotLayer];

      if (labelColumn && shouldShowLabels) {
        const labelColumnLower = labelColumn.toLowerCase().trim();
        const textLayer = new TextLayer({
          id: 'marker-labels',
          data: markers,
          pickable: false,
          getPosition: d => [d.longitude, d.latitude],
          getText: d => {
            const text = d[labelColumnLower];
            return text ? String(text) : '';
          },
          getSize: 12,
          getAngle: 0,
          getTextAnchor: 'start',
          getAlignmentBaseline: 'center',
          getPixelOffset: [10, 0], // Position to the right of marker
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'normal',
          background: false,
          getColor: [0, 0, 0, 255], // Black text
          outlineWidth: 2,
          outlineColor: [255, 255, 255, 255], // White outline for readability
        });
        layers.push(textLayer);
      }

      // Create or update overlay
      if (!overlayRef.current) {
        overlayRef.current = new GoogleMapsOverlay({
          layers: layers,
        });
        overlayRef.current.setMap(map);
      } else {
        overlayRef.current.setProps({
          layers: layers,
        });
      }

      // Notify that loading is complete
      if (onLoadComplete) {
        onLoadComplete();
      }
    }, 100); // 100ms delay to ensure map is ready

    // Cleanup function
    return () => {
      clearTimeout(initTimeout);
    };
    // Don't include selectedMarkers here - it's handled by the second useEffect to avoid full overlay recreation
  }, [map, markers, onMarkerClick, onLoadComplete, styleColumn, labelColumn, colorRefreshKey, shouldShowLabels]);

  // Update layer when selection or hover state changes (without recreating overlay)
  useEffect(() => {
    console.log('Selection or hover changed. Selected:', selectedMarkers.size, 'Hovered:', hoveredMarkerId);
    console.log('Selected marker IDs:', Array.from(selectedMarkers));
    if (overlayRef.current && markers.length > 0) {
      console.log('Creating new layer with updated selection/hover state...');

      // We need to recreate the layer with the new selection and hover states
      // Use timestamp to force deck.gl to treat it as a new layer
      const scatterplotLayer = new ScatterplotLayer({
        id: `markers-layer-${Date.now()}`,
        data: markers,
        pickable: true,
        opacity: 1,
        stroked: true,
        filled: true,
        radiusScale: 1,
        radiusMinPixels: 7,
        radiusMaxPixels: 7,  // Fixed size
        lineWidthMinPixels: 2,
        lineWidthMaxPixels: 30,  // Allow border to grow much larger
        lineWidthScale: 1,
        getPosition: d => [d.longitude, d.latitude],
        getRadius: 8,  // Fixed radius for all markers
        getFillColor: d => {
          // Keep original fill color always
          let color;
          if (styleColumn) {
            const styleColumnLower = styleColumn.toLowerCase().trim();
            const categoryValue = d[styleColumnLower];
            color = getCategoryColor(categoryValue);
          } else {
            color = getMarkerColor(d.color);
          }
          const rgb = hexToRgb(color);
          return rgb;
        },
        getLineColor: d => {
          const isSelected = selectedMarkers.has(d.id);
          const isHovered = hoveredMarkerId === d.id;

          // Get the fill color
          let color;
          if (styleColumn) {
            const styleColumnLower = styleColumn.toLowerCase().trim();
            const categoryValue = d[styleColumnLower];
            color = getCategoryColor(categoryValue);
          } else {
            color = getMarkerColor(d.color);
          }
          const fillRgb = hexToRgb(color);

          if (isSelected) {
            // Black border for selected (or white if fill is black)
            const isBlackFill = fillRgb[0] < 30 && fillRgb[1] < 30 && fillRgb[2] < 30;
            return isBlackFill ? [255, 255, 255] : [0, 0, 0];
          }

          if (isHovered) {
            // Opposite/inverse color for hover
            return getOppositeColor(fillRgb);
          }

          // Default white border
          return [255, 255, 255];
        },
        getLineWidth: d => {
          const isSelected = selectedMarkers.has(d.id);
          const isHovered = hoveredMarkerId === d.id;
          const width = isSelected ? 20 : (isHovered ? 8 : 2);
          if (isSelected || isHovered) {
            console.log('Marker', d.id, 'lineWidth:', width, 'selected:', isSelected, 'hovered:', isHovered);
          }
          return width;
        },
        onClick: info => {
          if (info.object) {
            onMarkerClick(info.object.id);
          }
        },
        onHover: info => {
          if (onHover) {
            onHover(info.object ? info.object.id : null);
          }
        },
        updateTriggers: {
          getRadius: [selectedMarkers, hoveredMarkerId],
          getFillColor: [styleColumn, selectedMarkers, hoveredMarkerId, colorRefreshKey],
          getLineColor: [selectedMarkers, hoveredMarkerId, colorRefreshKey],
          getLineWidth: [selectedMarkers, hoveredMarkerId],
        },
      });

      // Add text layer if labelColumn is set and zoom is sufficient
      const layers = [scatterplotLayer];

      if (labelColumn && shouldShowLabels) {
        const labelColumnLower = labelColumn.toLowerCase().trim();
        const textLayer = new TextLayer({
          id: `marker-labels-${Date.now()}`,
          data: markers,
          pickable: false,
          getPosition: d => [d.longitude, d.latitude],
          getText: d => {
            const text = d[labelColumnLower];
            return text ? String(text) : '';
          },
          getSize: 12,
          getAngle: 0,
          getTextAnchor: 'start',
          getAlignmentBaseline: 'center',
          getPixelOffset: [10, 0],
          fontFamily: 'Arial, sans-serif',
          fontWeight: 'normal',
          background: false,
          getColor: [0, 0, 0, 255],
          outlineWidth: 2,
          outlineColor: [255, 255, 255, 255],
        });
        layers.push(textLayer);
      }

      overlayRef.current.setProps({
        layers: layers,
      });
    }
  }, [hoveredMarkerId, selectedMarkers, markers, styleColumn, labelColumn, onMarkerClick, onHover, colorRefreshKey, shouldShowLabels]);

  // Cleanup overlay when component unmounts
  useEffect(() => {
    return () => {
      if (overlayRef.current) {
        overlayRef.current.finalize();
        overlayRef.current = null;
      }
    };
  }, []);

  return null; // This component doesn't render anything directly
}

// Component for handling selection drawing with shift+drag
function SelectionDrawing({ map, markers, onSelectionComplete }) {
  const [drawingPath, setDrawingPath] = useState([]);
  const polygonRef = useRef(null);
  const isDrawingRef = useRef(false);
  const drawingPathRef = useRef([]);
  const shiftKeyRef = useRef(false);

  // Keep drawingPathRef in sync with drawingPath
  useEffect(() => {
    drawingPathRef.current = drawingPath;
  }, [drawingPath]);

  useEffect(() => {
    if (!map) return;

    // Track shift key state and disable map dragging when shift is held
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') {
        shiftKeyRef.current = true;
        // Disable map dragging when shift is held
        map.setOptions({ draggable: false });
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        shiftKeyRef.current = false;
        // Re-enable map dragging when shift is released
        map.setOptions({ draggable: true });
        // Cancel any in-progress drawing
        if (isDrawingRef.current) {
          isDrawingRef.current = false;
          setDrawingPath([]);
          if (polygonRef.current) {
            polygonRef.current.setMap(null);
            polygonRef.current = null;
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      // Ensure map dragging is re-enabled on cleanup
      map.setOptions({ draggable: true });
    };
  }, [map]);

  // Handle shift+drag lasso selection
  useEffect(() => {
    if (!map) return;

    // Lasso mode - shift+drag to draw freeform polygon
    const handleMouseDown = (e) => {
      if (!e.latLng || !shiftKeyRef.current) return;

      console.log('=== LASSO START (Shift+Drag) ===');
      isDrawingRef.current = true;
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      setDrawingPath([[lng, lat]]);
    };

    const handleMouseMove = (e) => {
      if (!isDrawingRef.current || !e.latLng) return;

      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      // Add point to path (sample every point for smooth lasso)
      setDrawingPath(prev => [...prev, [lng, lat]]);
    };

    const handleMouseUp = (e) => {
      if (!isDrawingRef.current) return;

      console.log('=== LASSO END ===');
      const currentPath = drawingPathRef.current;
      console.log('Lasso path length:', currentPath.length);

      if (currentPath.length >= 3) {
        // Complete lasso and select markers
        const selected = markers.filter(m =>
          pointInPolygon([m.longitude, m.latitude], currentPath)
        );

        console.log('Selected', selected.length, 'markers');
        onSelectionComplete(new Set(selected.map(m => m.id)));

        // Clean up polygon
        if (polygonRef.current) {
          polygonRef.current.setMap(null);
          polygonRef.current = null;
        }
      }

      isDrawingRef.current = false;
      setDrawingPath([]);
    };

    const mouseDownListener = map.addListener('mousedown', handleMouseDown);
    const mouseMoveListener = map.addListener('mousemove', handleMouseMove);
    const mouseUpListener = map.addListener('mouseup', handleMouseUp);

    return () => {
      window.google.maps.event.removeListener(mouseDownListener);
      window.google.maps.event.removeListener(mouseMoveListener);
      window.google.maps.event.removeListener(mouseUpListener);
    };
  }, [map, markers, onSelectionComplete]);

  // Update visual lasso polygon
  useEffect(() => {
    if (!map || !isDrawingRef.current || drawingPath.length === 0) return;

    // Create or update polygon - show fixed path only
    const path = drawingPath.map(([lng, lat]) => ({ lat, lng }));

    if (!polygonRef.current) {
      polygonRef.current = new window.google.maps.Polygon({
        strokeColor: '#FF0000',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#FF0000',
        fillOpacity: 0.15,
        map: map,
        clickable: false  // Don't intercept clicks
      });
    }

    polygonRef.current.setPath(path);
  }, [map, drawingPath]);

  // Handle right-click to clear selections
  useEffect(() => {
    if (!map) return;

    const handleRightClick = (e) => {
      // Prevent default context menu
      e.stop();

      console.log('=== RIGHT CLICK - Clearing selections ===');
      // Clear all selections by passing empty Set
      onSelectionComplete(new Set());
    };

    const rightClickListener = map.addListener('rightclick', handleRightClick);

    return () => {
      window.google.maps.event.removeListener(rightClickListener);
    };
  }, [map, onSelectionComplete]);

  return null;
}

// Wrapper component to use useMap hook
function MapSelectionHandler({ markers, onSelectionComplete }) {
  const map = useMap();

  return (
    <SelectionDrawing
      map={map}
      markers={markers}
      onSelectionComplete={onSelectionComplete}
    />
  );
}

function DeckGLMap(props = {}) {
  const {
    markers = [],
    selectedMarkers = emptySet,
    onMarkerUpdate = noop,
    onSelectionChange = noop,
    sheetConfig = null,
    colorRefreshKey = 0,
    onOpenSettings = noop
  } = props;
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [colorLegend, setColorLegend] = useState(emptyMap);
  const [shortcutsExpanded, setShortcutsExpanded] = useState(false);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Reset loading state when markers change
  useEffect(() => {
    if (markers && markers.length > 0) {
      setIsLoading(true);
    }
  }, [markers]);

  // Calculate map center from markers
  const defaultCenter = useMemo(() => {
    if (!markers || markers.length === 0) {
      return { lat: 39.8283, lng: -98.5795 }; // Center of USA
    }
    // Filter to only markers with valid, finite coordinates
    const validMarkers = markers.filter(m =>
      typeof m.latitude === 'number' &&
      typeof m.longitude === 'number' &&
      isFinite(m.latitude) &&
      isFinite(m.longitude)
    );

    if (validMarkers.length === 0) {
      console.warn('No markers with valid coordinates found');
      return { lat: 39.8283, lng: -98.5795 }; // Center of USA
    }

    // Calculate average position
    const sum = validMarkers.reduce(
      (acc, m) => ({
        lat: acc.lat + m.latitude,
        lng: acc.lng + m.longitude,
      }),
      { lat: 0, lng: 0 }
    );
    return {
      lat: sum.lat / validMarkers.length,
      lng: sum.lng / validMarkers.length,
    };
  }, [markers]);

  const handleMarkerClick = useCallback((markerId) => {
    // Toggle selection on map click
    const newSelection = new Set(selectedMarkers);
    if (newSelection.has(markerId)) {
      newSelection.delete(markerId);
    } else {
      newSelection.add(markerId);
    }
    onSelectionChange(newSelection);
  }, [selectedMarkers, onSelectionChange]);

  const handleMarkerHover = useCallback((markerId) => {
    setHoveredMarkerId(markerId);
  }, []);

  const handleLoadComplete = useCallback(() => {
    setIsLoading(false);
    // Update legend with current color assignments
    if (sheetConfig?.styleColumn) {
      setColorLegend(getColorAssignments());
    }
  }, [sheetConfig?.styleColumn]);

  const handleSelectionComplete = useCallback((selectedIds) => {
    onSelectionChange(selectedIds);
  }, [onSelectionChange]);

  return (
    <div className="map-wrapper">

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-message">
            Loading {markers.length.toLocaleString()} markers...
          </div>
        </div>
      )}

      <APIProvider apiKey={apiKey}>
        <GoogleMap
          mapId={MAP_ID}
          defaultCenter={defaultCenter}
          defaultZoom={4}
          gestureHandling="greedy"
          disableDefaultUI={false}
          style={{ width: '100%', height: '100%' }}
        >
          <DeckGLOverlay
            markers={markers}
            selectedMarkers={selectedMarkers}
            onMarkerClick={handleMarkerClick}
            onHover={handleMarkerHover}
            hoveredMarkerId={hoveredMarkerId}
            onLoadComplete={handleLoadComplete}
            styleColumn={sheetConfig?.styleColumn}
            labelColumn={sheetConfig?.labelColumn}
            colorRefreshKey={colorRefreshKey}
          />
          <MapSelectionHandler
            markers={markers}
            onSelectionComplete={handleSelectionComplete}
          />
        </GoogleMap>
      </APIProvider>

      {/* Settings and Shortcuts buttons */}
      <div className="keyboard-shortcuts-container">
        {!shortcutsExpanded && (
          <>
            <button
              className="keyboard-shortcuts-button settings-button"
              onClick={onOpenSettings}
              title="Sheet Settings"
            >
              ⚙️
            </button>
            <button
              className="keyboard-shortcuts-button"
              onClick={() => setShortcutsExpanded(true)}
              title="Keyboard Shortcuts"
            >
              Shortcuts
            </button>
          </>
        )}
        {shortcutsExpanded && (
          <div className="keyboard-shortcuts-info">
            <div className="shortcuts-header">
              <strong>Keyboard Shortcuts</strong>
              <button
                className="shortcuts-close"
                onClick={() => setShortcutsExpanded(false)}
              >
                ×
              </button>
            </div>
            <div className="shortcuts-content">
              <div className="shortcut-item">
                <span className="shortcut-key">Shift + Drag</span>
                <span className="shortcut-desc">Lasso Select</span>
              </div>
              <div className="shortcut-item">
                <span className="shortcut-key">Right Click</span>
                <span className="shortcut-desc">Clear Selection</span>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

export default DeckGLMap;
